import { type App, ref } from "vue"
import { EMBED_MODE_KEY, type EmbedModeContext } from "../composables/embed/useEmbedMode"
import { createMessage, detectParentOrigin } from "@vitable/connect/core"
import type { MessageDefKey, HasPayload, PayloadOf } from "@vitable/connect/core"

export interface EmbedInitOptions {
  pathPrefix?: string
}

export function isEmbeddedContext(): boolean {
  return window.self !== window.top
}

// --- Module-level sender for non-Vue contexts (Axios interceptors, useEmbedAuth) ---

let capturedParentOrigin: string | null = null

export function setCapturedOrigin(origin: string): void {
  if (capturedParentOrigin !== null && capturedParentOrigin !== origin) {
    console.warn("[embed-service] Origin already captured, ignoring re-set attempt")
    return
  }
  capturedParentOrigin = origin
}

export function __getCapturedOrigin(): string | null {
  return capturedParentOrigin
}

export function sendToParent<K extends MessageDefKey>(
  type: K,
  ...args: HasPayload<K> extends true ? [PayloadOf<K>] : []
): void {
  if (!capturedParentOrigin) {
    console.warn("[embed-service] sendToParent: origin not yet captured — message dropped:", type)
    return
  }

  const message = createMessage(type, ...args)
  window.parent.postMessage(message, capturedParentOrigin)
}

export function __resetEmbedService(): void {
  capturedParentOrigin = null
}

// --- End module-level sender ---

function initializeEmbedContext(app: App, options: EmbedInitOptions = {}): void {
  const { pathPrefix = "/embed" } = options

  const isEmbedPath = window.location.pathname.startsWith(pathPrefix)
  const isInIframe = isEmbeddedContext()

  // Provide embed context if EITHER on an embed path OR running in an iframe.
  // The iframe check handles page refresh during enrollment (routes like
  // /background, /enroll/benefits are outside /embed/* but still in iframe).
  if (!isEmbedPath && !isInIframe) {
    return
  }

  const embedModeContext: EmbedModeContext = {
    isEmbedded: ref(isInIframe),
    parentOrigin: ref(detectParentOrigin()),
    isInitialized: ref(false)
  }

  app.provide(EMBED_MODE_KEY, embedModeContext)

  if (isInIframe) {
    setTimeout(() => {
      embedModeContext.isInitialized.value = true
      // SECURITY NOTE: We intentionally use "*" as the target origin for the initial
      // VITABLE_CONNECT_READY message because:
      // 1. At this point, parentOrigin may not be reliably detected (document.referrer
      //    can be empty or blocked by cross-origin policies)
      // 2. The READY message contains no sensitive data - it only signals iframe load
      // 3. The parent SDK validates this message via its allowedOrigins configuration
      // 4. All subsequent messages use the detected parentOrigin once the handshake completes
      window.parent.postMessage(createMessage("VITABLE_CONNECT_READY"), "*")
    }, 0)
  }
}

export default { initializeEmbedContext }
